import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import LocalShipping from '@mui/icons-material/LocalShipping';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Inventory from '@mui/icons-material/Inventory';
import Place from '@mui/icons-material/Place';
import Person from '@mui/icons-material/Person';
import Phone from '@mui/icons-material/Phone';
import ArrowBack from '@mui/icons-material/ArrowBack';
import WorkspacePremiumOutlined from '@mui/icons-material/WorkspacePremiumOutlined';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useAuth } from '../context/AuthContext';
import { useOrders } from '../context/OrderContext';
import { supabase } from '../services/supabaseClient';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

// Type assertion for the icon fix
const DefaultIcon = L.icon({
    iconUrl: icon as unknown as string,
    shadowUrl: iconShadow as unknown as string,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const steps = ['Order Placed', 'Confirmed', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered'];

const getStepIndex = (status: string) => {
  switch (status.toLowerCase()) {
    case 'pending': return 0;
    case 'confirmed': return 1;
    case 'processing': return 2;
    case 'shipped': return 3;
    case 'out_for_delivery': return 4;
    case 'delivered': return 5;
    default: return 0;
  }
};

const getProtectionLabel = (status?: string | null) => {
  switch ((status || '').toLowerCase()) {
    case 'held':
      return 'Protected Funds Held';
    case 'release_requested':
      return 'Release Requested';
    case 'refund_requested':
      return 'Under Review';
    case 'released':
    case 'paid_out':
      return 'Released to Seller';
    case 'paid':
      return 'Paid';
    case 'pending':
      return 'Payment Pending';
    case 'failed':
      return 'Payment Failed';
    default:
      return 'Protection Pending';
  }
};

const getProtectionColor = (status?: string | null) => {
  switch ((status || '').toLowerCase()) {
    case 'held':
      return 'warning';
    case 'release_requested':
      return 'info';
    case 'refund_requested':
    case 'failed':
      return 'error';
    case 'released':
    case 'paid_out':
    case 'paid':
      return 'success';
    default:
      return 'default';
  }
};

const OrderTracking: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getOrderById } = useOrders();
  
  const order = orderId ? getOrderById(orderId) : null;
  const [activeStep, setActiveStep] = useState(0);
  const [statusEvents, setStatusEvents] = useState<Array<{ id: string; status: string; created_at: string }>>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [escrowHolds, setEscrowHolds] = useState<Array<{
    id: string;
    status: string;
    amount: number;
    currency: string;
    is_perishable: boolean;
    refund_deadline: string | null;
    created_at: string;
  }>>([]);
  const [loadingEscrow, setLoadingEscrow] = useState(false);
  const [escrowError, setEscrowError] = useState<string | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [submittingEscrowAction, setSubmittingEscrowAction] = useState(false);
  const [refundRequests, setRefundRequests] = useState<Array<{
    id: string;
    reason: string | null;
    status: string;
    resolution_notes: string | null;
    seller_response: string | null;
    seller_responded_at: string | null;
    resolved_at: string | null;
    evidence_metadata: any[] | null;
    created_at: string;
  }>>([]);
  const [loadingRefundRequests, setLoadingRefundRequests] = useState(false);
  const [refundRefreshToken, setRefundRefreshToken] = useState(0);
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [evidenceOpeningPath, setEvidenceOpeningPath] = useState<string | null>(null);

  useEffect(() => {
    if (order) {
      setActiveStep(getStepIndex(order.status));
    }
  }, [order]);

  useEffect(() => {
    const loadEvents = async () => {
      if (!order) return;
      setLoadingEvents(true);
      try {
        const { data, error } = await supabase
          .from('order_status_events')
          .select('id, status, created_at')
          .eq('order_id', order.id)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;
        setStatusEvents(data || []);
      } catch {
        setStatusEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    };

    void loadEvents();
  }, [order?.id]);

  useEffect(() => {
    const loadEscrow = async () => {
      if (!order || !user) return;
      setLoadingEscrow(true);
      setEscrowError(null);
      try {
        const { data, error } = await supabase
          .from('escrow_holds')
          .select('id, status, amount, currency, is_perishable, refund_deadline, created_at')
          .eq('order_id', order.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setEscrowHolds((data as any[]) || []);
      } catch (e: any) {
        setEscrowHolds([]);
        setEscrowError(e?.message || 'Failed to load escrow details.');
      } finally {
        setLoadingEscrow(false);
      }
    };

    void loadEscrow();
  }, [order?.id, user?.id]);

  const latestHold = escrowHolds[0] ?? null;
  const latestRefundRequest = refundRequests[0] ?? null;
  const latestRefundEvidence = Array.isArray(latestRefundRequest?.evidence_metadata)
    ? (latestRefundRequest?.evidence_metadata as any[])
    : [];

  const sanitizeEvidenceFileName = (fileName: string) => fileName.replace(/[^a-zA-Z0-9._-]/g, '_');

  const handleOpenEvidence = async (storagePath: string) => {
    setEscrowError(null);
    setEvidenceOpeningPath(storagePath);
    try {
      const { data, error } = await supabase.storage
        .from('marketplace-protection-evidence')
        .createSignedUrl(storagePath, 60 * 10);

      if (error) {
        throw new Error(
          error.message.includes('Bucket not found') || error.message.includes('row-level security')
            ? 'Apply the marketplace protection evidence storage migration before opening evidence files.'
            : error.message || 'Evidence link could not be generated.',
        );
      }

      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setEscrowError(e?.message || 'Evidence could not be opened.');
    } finally {
      setEvidenceOpeningPath(null);
    }
  };

  const handleUploadEvidence = async () => {
    if (!user || !latestRefundRequest?.id || !evidenceFile) return;

    setEscrowError(null);
    setEvidenceUploading(true);
    try {
      const safeName = sanitizeEvidenceFileName(evidenceFile.name);
      const storagePath = `${user.id}/${latestRefundRequest.id}/${Date.now()}_${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from('marketplace-protection-evidence')
        .upload(storagePath, evidenceFile, { upsert: false });

      if (uploadError) {
        throw new Error(
          uploadError.message.includes('Bucket not found') || uploadError.message.includes('row-level security')
            ? 'Apply the marketplace protection evidence storage migration before uploading evidence files.'
            : uploadError.message || 'Evidence file could not be uploaded.',
        );
      }

      const { error: attachError } = await supabase.rpc('attach_marketplace_protection_evidence', {
        p_refund_request_id: latestRefundRequest.id,
        p_entry: {
          storage_path: storagePath,
          file_name: evidenceFile.name,
          mime_type: evidenceFile.type || null,
          size_bytes: evidenceFile.size,
        },
      });

      if (attachError) throw attachError;

      setEvidenceFile(null);
      setRefundRefreshToken((prev) => prev + 1);
    } catch (e: any) {
      setEscrowError(e?.message || 'Evidence could not be uploaded.');
    } finally {
      setEvidenceUploading(false);
    }
  };

  useEffect(() => {
    const loadRefundRequests = async () => {
      if (!latestHold?.id || !user) {
        setRefundRequests([]);
        return;
      }

      setLoadingRefundRequests(true);
      try {
        const { data, error } = await supabase
          .from('refund_requests')
          .select('id, reason, status, resolution_notes, seller_response, seller_responded_at, resolved_at, evidence_metadata, created_at')
          .eq('escrow_hold_id', latestHold.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setRefundRequests((data as any[]) || []);
      } catch {
        setRefundRequests([]);
      } finally {
        setLoadingRefundRequests(false);
      }
    };

    void loadRefundRequests();
  }, [latestHold?.id, user?.id, refundRefreshToken]);

  const handleConfirmDelivery = async () => {
    if (!latestHold || !user) return;
    setSubmittingEscrowAction(true);
    setEscrowError(null);
    try {
      const { error } = await supabase.rpc('request_marketplace_escrow_release', {
        p_escrow_hold_id: latestHold.id,
      });

      if (error) throw error;

      setEscrowHolds((prev) => prev.map((h) => (h.id === latestHold.id ? { ...h, status: 'release_requested' } : h)));
      setRefundRefreshToken((prev) => prev + 1);
    } catch (e: any) {
      setEscrowError(e?.message || 'Failed to confirm delivery.');
    } finally {
      setSubmittingEscrowAction(false);
    }
  };

  const handleRequestRefund = async () => {
    if (!latestHold || !user) return;
    const trimmed = refundReason.trim();
    if (!trimmed) {
      setEscrowError('Please add a refund reason.');
      return;
    }

    setSubmittingEscrowAction(true);
    setEscrowError(null);
    try {
      const { error } = await supabase.rpc('submit_marketplace_refund_request', {
        p_escrow_hold_id: latestHold.id,
        p_reason: trimmed,
      });

      if (error) throw error;

      setEscrowHolds((prev) => prev.map((h) => (h.id === latestHold.id ? { ...h, status: 'refund_requested' } : h)));
      setRefundDialogOpen(false);
      setRefundReason('');
      setRefundRefreshToken((prev) => prev + 1);
    } catch (e: any) {
      setEscrowError(e?.message || 'Failed to submit refund request.');
    } finally {
      setSubmittingEscrowAction(false);
    }
  };

  if (!orderId) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">No Order ID provided</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/account')} sx={{ mt: 2 }}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading order details...</Typography>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">Please sign in to view order protection details.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/account')} sx={{ mt: 2 }}>
          Back
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Button 
        startIcon={<ArrowBack />} 
        onClick={() => navigate('/account')} 
        sx={{ mb: 2 }}
      >
        Back to Orders
      </Button>

      <Grid container spacing={3}>
        {/* Order Status Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Order #{order.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Placed on {new Date(order.date).toLocaleDateString()}
                </Typography>
              </Box>
              <Chip 
                label={order.status.replace('_', ' ').toUpperCase()} 
                color={order.status === 'delivered' ? 'success' : 'primary'} 
                sx={{ fontWeight: 'bold' }}
              />
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {order.status !== 'delivered' && (
              <Box sx={{ mt: 4, p: 2, bgcolor: 'primary.50', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                <LocalShipping color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Estimated Delivery
                  </Typography>
                  <Typography variant="body2">
                    {new Date(new Date(order.date).setDate(new Date(order.date).getDate() + 3)).toDateString()}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Order Details & Map */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Tracking Updates
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <List>
              {loadingEvents && (
                <ListItem>
                  <ListItemIcon><Inventory /></ListItemIcon>
                  <ListItemText primary="Loading tracking updates..." />
                </ListItem>
              )}

              {!loadingEvents && statusEvents.length > 0 && statusEvents.map((e) => (
                <ListItem key={e.id}>
                  <ListItemIcon>
                    {e.status === 'delivered'
                      ? <CheckCircle color="success" />
                      : e.status === 'shipped'
                        ? <LocalShipping color="primary" />
                        : <Inventory />}
                  </ListItemIcon>
                  <ListItemText
                    primary={e.status.replace(/_/g, ' ').toUpperCase()}
                    secondary={new Date(e.created_at).toLocaleString()}
                  />
                </ListItem>
              ))}

              {!loadingEvents && statusEvents.length === 0 && (
                <>
                  {activeStep >= 3 && (
                    <ListItem>
                      <ListItemIcon><LocalShipping color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Package has left the facility" 
                        secondary={`${new Date(order.date).toLocaleDateString()} - 10:00 AM`} 
                      />
                    </ListItem>
                  )}
                  {activeStep >= 2 && (
                    <ListItem>
                      <ListItemIcon><Inventory /></ListItemIcon>
                      <ListItemText 
                        primary="Order processed and packed" 
                        secondary={`${new Date(order.date).toLocaleDateString()} - 09:30 AM`} 
                      />
                    </ListItem>
                  )}
                  <ListItem>
                    <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                    <ListItemText 
                      primary="Order confirmed" 
                      secondary={`${new Date(order.date).toLocaleDateString()} - 08:00 AM`} 
                    />
                  </ListItem>
                </>
              )}
            </List>

            <Box sx={{ mt: 3 }}>
               <Typography variant="h6" gutterBottom>
                 Live Location
               </Typography>
               <Box 
                 sx={{ 
                   height: 400, 
                   width: '100%',
                   borderRadius: 1,
                   overflow: 'hidden',
                   '& .leaflet-container': {
                     height: '100%',
                     width: '100%',
                     zIndex: 0
                   }
                 }}
               >
                 <MapContainer center={[-1.2921, 36.8219]} zoom={13} scrollWheelZoom={false}>
                   <TileLayer
                     attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                     url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                   />
                   <Marker position={[-1.2921, 36.8219]}>
                     <Popup>
                       Order #{order.id} <br /> Current Location
                     </Popup>
                   </Marker>
                 </MapContainer>
               </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Delivery Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Delivery Address
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Person sx={{ color: 'text.secondary', mr: 2 }} />
              <Typography>{(order as any)?.shippingDetails?.fullName || user?.name || 'Customer Name'}</Typography>
            </Box>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Place sx={{ color: 'text.secondary', mr: 2 }} />
              <Typography>
                {order.shippingAddress || 'Nairobi, Kenya'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <Phone sx={{ color: 'text.secondary', mr: 2 }} />
              <Typography>{(order as any)?.shippingDetails?.phone || '+254 712 345 678'}</Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Payment Protection
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {escrowError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {escrowError}
              </Alert>
            )}

            {loadingEscrow ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">Loading protection details...</Typography>
              </Box>
            ) : !latestHold ? (
              <Alert severity={(order as any).paymentStatus === 'paid' ? 'warning' : 'info'}>
                {((order as any).paymentStatus === 'paid')
                  ? 'Payment is marked as paid, but the order is not yet in protected hold mode. Buyer protection only becomes active once an escrow hold record exists.'
                  : 'Protection activates after successful payment confirmation and escrow hold creation.'}
              </Alert>
            ) : (
              <>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2, borderRadius: 3, bgcolor: 'warning.50', borderColor: 'warning.200' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Protection Status
                      </Typography>
                      <Typography variant="h6" fontWeight={800}>
                        Funds secured on-platform
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Your payment is held while delivery is completed and the review window remains open.
                      </Typography>
                    </Box>
                    <Chip
                      icon={<WorkspacePremiumOutlined />}
                      label={getProtectionLabel(latestHold.status)}
                      color={getProtectionColor(latestHold.status) as any}
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                </Paper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Hold Status</Typography>
                  <Chip label={latestHold.status.replace(/_/g, ' ').toUpperCase()} size="small" />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Amount</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {latestHold.currency} {Number(latestHold.amount).toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Refund Deadline</Typography>
                  <Typography variant="body2">
                    {latestHold.refund_deadline ? new Date(latestHold.refund_deadline).toLocaleString() : 'Not set'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="contained"
                    disabled={submittingEscrowAction || order.status !== 'delivered' || ['refund_requested', 'released', 'paid_out'].includes(latestHold.status)}
                    onClick={() => void handleConfirmDelivery()}
                  >
                    Confirm Delivery
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    disabled={submittingEscrowAction || ['refund_requested', 'released', 'paid_out'].includes(latestHold.status)}
                    onClick={() => { setEscrowError(null); setRefundDialogOpen(true); }}
                  >
                    Request Refund
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Confirm delivery to trigger release. Request a refund before the deadline if there’s an issue.
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Protection Review Timeline
                </Typography>

                {loadingRefundRequests ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading protection review activity...
                  </Typography>
                ) : !latestRefundRequest ? (
                  <Typography variant="body2" color="text.secondary">
                    No dispute or refund review has been opened on this protection record.
                  </Typography>
                ) : (
                  <List disablePadding>
                    <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={`Buyer request • ${latestRefundRequest.status.replace(/_/g, ' ').toUpperCase()}`}
                        secondary={`${new Date(latestRefundRequest.created_at).toLocaleString()}${latestRefundRequest.reason ? ` • ${latestRefundRequest.reason}` : ''}`}
                      />
                    </ListItem>
                    {latestRefundEvidence.length > 0 && (
                      <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemText
                          primary={`Evidence attached • ${latestRefundEvidence.length}`}
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                              {latestRefundEvidence
                                .filter((entry) => entry && typeof entry === 'object' && typeof entry.storage_path === 'string')
                                .slice(0, 5)
                                .map((entry, idx) => (
                                  <Button
                                    key={`${entry.storage_path}-${idx}`}
                                    variant="text"
                                    sx={{ justifyContent: 'flex-start', px: 0, textTransform: 'none' }}
                                    disabled={evidenceOpeningPath === entry.storage_path}
                                    onClick={() => void handleOpenEvidence(entry.storage_path)}
                                  >
                                    {evidenceOpeningPath === entry.storage_path ? 'Opening...' : entry.file_name || 'Open evidence'}
                                  </Button>
                                ))}
                              {latestRefundEvidence.length > 5 && (
                                <Typography variant="caption" color="text.secondary">
                                  Showing first 5 evidence items.
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                    {latestRefundRequest.seller_response && (
                      <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemText
                          primary="Seller response received"
                          secondary={`${latestRefundRequest.seller_responded_at ? new Date(latestRefundRequest.seller_responded_at).toLocaleString() : 'Pending timestamp'} • ${latestRefundRequest.seller_response}`}
                        />
                      </ListItem>
                    )}
                    {latestRefundRequest.resolution_notes && (
                      <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemText
                          primary="Platform review decision"
                          secondary={`${latestRefundRequest.resolved_at ? new Date(latestRefundRequest.resolved_at).toLocaleString() : 'Awaiting timestamp'} • ${latestRefundRequest.resolution_notes}`}
                        />
                      </ListItem>
                    )}
                    {!latestRefundRequest.resolved_at && (
                      <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                        <ListItemText
                          primary="Attach more evidence"
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                              <Button variant="outlined" component="label" disabled={evidenceUploading}>
                                {evidenceFile ? evidenceFile.name : 'Choose file'}
                                <input
                                  type="file"
                                  hidden
                                  accept="image/*,application/pdf,text/plain"
                                  onChange={(e) => setEvidenceFile(e.target.files?.[0] || null)}
                                />
                              </Button>
                              <Button
                                variant="contained"
                                disabled={!evidenceFile || evidenceUploading}
                                onClick={() => void handleUploadEvidence()}
                              >
                                {evidenceUploading ? 'Uploading...' : 'Upload evidence'}
                              </Button>
                              <Typography variant="caption" color="text.secondary">
                                Attach photos or PDFs that help platform review your request.
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                  </List>
                )}
              </>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Order Items
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List disablePadding>
              {order.items.map((item: any, index: number) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemText 
                    primary={item.name} 
                    secondary={`Qty: ${item.quantity} x KSh ${item.price}`} 
                  />
                  <Typography fontWeight="bold">
                    KSh {(item.price * item.quantity).toLocaleString()}
                  </Typography>
                </ListItem>
              ))}
              <Divider sx={{ my: 1 }} />
              <ListItem sx={{ px: 0 }}>
                <ListItemText primary="Total" primaryTypographyProps={{ fontWeight: 'bold' }} />
                <Typography fontWeight="bold" color="primary.main">
                  KSh {order.total.toLocaleString()}
                </Typography>
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Request a Refund</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Describe the issue. This request will be linked to your order protection record.
          </Typography>
          <TextField
            label="Reason"
            value={refundReason}
            onChange={(e) => setRefundReason(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} disabled={submittingEscrowAction}>Cancel</Button>
          <Button
            onClick={() => void handleRequestRefund()}
            variant="contained"
            color="error"
            disabled={submittingEscrowAction}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrderTracking;
